import { useState } from 'react'
import { agentEditorCopy } from '@/copy'
import { useNow } from '@/hooks/useNow'
import type { PromptVersion } from '@/types/api'
import { Button } from '@/ui/Button/Button'
import { CodeBlock } from '@/ui/CodeBlock/CodeBlock'
import { cx } from '@/ui/cx'
import { Disclosure } from '@/ui/Disclosure/Disclosure'
import { Inline } from '@/ui/layout/Inline'
import { Stack } from '@/ui/layout/Stack'
import { Text } from '@/ui/layout/Text'
import { SectionHeader } from '@/ui/SectionHeader/SectionHeader'
import { type DiffOp, diffLines } from '@/utils/diff'
import { formatRelative } from '@/utils/time'
import type { AgentDiff } from './diff'
import styles from './SharedPanels.module.css'

interface DiffLine {
  op: DiffOp
  text: string
}

/** Diff lines as the code block shows them: "+ " for an addition, "- " else. */
function DiffLines({ lines }: { lines: DiffLine[] }) {
  return lines.map((l, i) => (
    // biome-ignore lint/suspicious/noArrayIndexKey: diff lines are derived, no stable id
    <span key={i} className={styles.line} data-op={l.op}>
      {l.op === 'add' ? '+ ' : '- '}
      {l.text}
    </span>
  ))
}

/** Collapsible preview of unsaved edits, shown above the action row. */
export function DirtyDiffPanel({ diff }: { diff: AgentDiff }) {
  if (diff.total === 0) {
    return (
      <Text variant="caption" tone="text-ghost">
        {agentEditorCopy.history.noChanges}
      </Text>
    )
  }

  const hasCaps =
    diff.capabilities.added.length > 0 || diff.capabilities.removed.length > 0

  return (
    <Disclosure label={agentEditorCopy.unsavedChanges} count={diff.total}>
      <Stack gap="space-12">
        {diff.rows.map((r) => (
          <div key={`${r.label}:${r.from}:${r.to}`} className={styles.diffRow}>
            <Text tone="text-ghost" className={styles.wrap}>
              {r.label}
            </Text>
            <Text
              variant="code"
              tone="text-ghost"
              className={cx(styles.wrap, styles.del)}
            >
              {r.from}
            </Text>
            <Text variant="code" tone="text-ghost">
              →
            </Text>
            <Text variant="code" tone="text" className={styles.wrap}>
              {r.to}
            </Text>
          </div>
        ))}

        {hasCaps && (
          <Text as="div">
            <Text tone="text-ghost">
              {agentEditorCopy.history.capabilitiesLead}{' '}
            </Text>
            {diff.capabilities.added.map((c) => (
              <Text key={`a-${c}`} variant="code" tone="text">
                +{c}{' '}
              </Text>
            ))}
            {diff.capabilities.removed.map((c) => (
              <Text
                key={`r-${c}`}
                variant="code"
                tone="text-ghost"
                className={styles.del}
              >
                -{c}{' '}
              </Text>
            ))}
          </Text>
        )}

        {diff.prompt && (
          <CodeBlock
            title={agentEditorCopy.history.promptTitle}
            caption={agentEditorCopy.history.promptCaption(
              diff.prompt.added,
              diff.prompt.deleted,
            )}
            copyable={false}
            maxLines={8}
          >
            <DiffLines lines={diff.prompt.lines} />
          </CodeBlock>
        )}
      </Stack>
    </Disclosure>
  )
}

/**
 * Prompt version history. Each row is an immutable snapshot; expanding shows
 * the line diff vs the current editor prompt (what restore would apply), and
 * "restore" loads the snapshot into the editor without saving — saving then
 * mints a new version rather than mutating history.
 */
export function PromptHistoryList({
  versions,
  currentPrompt,
  onRestore,
}: {
  versions: PromptVersion[]
  currentPrompt: string
  onRestore: (prompt: string) => void
}) {
  const [selected, setSelected] = useState<number | null>(null)
  // Refresh "saved Xm ago" labels periodically so they don't freeze.
  const now = useNow()

  return (
    <Stack gap="space-8">
      <SectionHeader as="h3">{agentEditorCopy.history.heading}</SectionHeader>
      {versions.map((v) => {
        const open = selected === v.seq
        const diff = open
          ? diffLines(currentPrompt, v.prompt).filter((l) => l.op !== 'eq')
          : null
        return (
          <Disclosure
            key={v.seq}
            open={open}
            ground="surface"
            onOpenChange={(o) => setSelected(o ? v.seq : null)}
            label={
              <Inline gap="space-8">
                <Text variant="code" weight="strong">
                  {agentEditorCopy.history.version(v.seq)}
                </Text>
                <Text>
                  {v.created_by ?? agentEditorCopy.history.systemAuthor}
                </Text>
                <Text variant="caption" tone="text-ghost" numeric>
                  {formatRelative(v.at_ms, now)}
                </Text>
              </Inline>
            }
          >
            <Stack gap="space-12">
              {diff && diff.length === 0 ? (
                <Text variant="caption" tone="text-ghost">
                  {agentEditorCopy.history.identical}
                </Text>
              ) : (
                diff && (
                  <CodeBlock
                    title={agentEditorCopy.history.version(v.seq)}
                    caption={agentEditorCopy.history.diffCaption}
                    copyable={false}
                    maxLines={8}
                  >
                    <DiffLines lines={diff} />
                  </CodeBlock>
                )
              )}
              <Inline gap="space-8">
                <Button
                  variant="ghost"
                  ground="surface"
                  onClick={() => onRestore(v.prompt)}
                >
                  {agentEditorCopy.history.restore}
                </Button>
              </Inline>
            </Stack>
          </Disclosure>
        )
      })}
    </Stack>
  )
}
